// Merge another catalog item into this one.
//
// WHY THIS IS ITS OWN DOOR, next to Rename rather than inside it
// Rename changes one item's string. Merge ends another item's existence and files
// its entire history, stock and boxes under this one. They share the alias
// mechanism (the losing string keeps scanning) and nothing else, and folding merge
// into the rename form would put "delete an item" one mis-click from "fix a typo".
//
// It is admin-only, matching Rename and for a stronger version of the same reason:
// the string being retired is printed on physical labels, and the rows being moved
// are somebody's stock.
//
// THE REASSURANCE IS THE COPY'S JOB. Nobody merges two items unless they believe
// the shelf survives it, and the honest answer is that it does: every unit is
// summed onto this item, every box comes with it, and every lookup of the old
// string falls through to the alias. What is NOT reversible is the deletion of the
// other row, and the copy says so rather than burying it.
//
// The pre-check is `sku-siblings`, the same endpoint that warns at mint time, so
// the two duplicates a person is most likely to want merged are already on screen.

import React, { useEffect, useMemo, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { RadioGroup, RadioRow } from '../components/ui/Radio';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import Dialog from '../components/ui/Dialog';
import { Alert } from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import ItemName, { itemLabel, type ItemNameSource } from '../components/ItemName';

interface Sibling {
  id: number;
  sku: string;
  description: string;
  exact: boolean;
  /** Joined name fields, when the endpoint carries them. ItemName falls back to
   *  the description and then the SKU, so an older payload still reads right. */
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
}

interface MergeResult {
  winner: { id: number; sku: string };
  loser_sku: string;
  inventory_merged: number;
  batch_assignments_moved: number;
  batch_assignments_kept: string[];
  batchserials_already_minted: string[];
  scan_drafts_rewritten: number;
  moved: Record<string, number>;
}

interface Props {
  itemId: number;
  /** The SURVIVING item's code. Still the key the sibling lookup runs on, and
   *  still what the merge is filed against. */
  currentSku: string;
  /** The surviving item, for the title. Optional so the code alone remains a
   *  working call. */
  currentItem?: ItemNameSource;
  onClose: () => void;
  onMerged: (result: MergeResult) => void;
}

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

const MergeItemModal: React.FC<Props> = ({
  itemId,
  currentSku,
  currentItem,
  onClose,
  onMerged,
}) => {
  const api = useApi();
  const toast = useToast();

  const [siblings, setSiblings] = useState<Sibling[]>([]);
  const [loserId, setLoserId] = useState<string>('');
  const [typedSku, setTypedSku] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // The likely candidates, from the same check that warns at mint time. Anything
  // else is reachable by typing the SKU, because a duplicate that predates the
  // guard may not match either grammar.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = (await api.get(
          `/parts-catalog/sku-siblings?sku=${encodeURIComponent(currentSku)}`
        )) as { rows: Sibling[] };
        if (!cancelled) setSiblings(data.rows || []);
      } catch {
        // Additive. Typing the SKU still works.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSku]);

  const chosen = useMemo(
    () => siblings.find((s) => String(s.id) === loserId) || null,
    [siblings, loserId]
  );

  const canSubmit = (!!loserId || !!typedSku.trim()) && !!reason.trim() && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      let targetId = loserId ? parseInt(loserId, 10) : NaN;

      // A typed SKU is resolved through the catalog list rather than trusted, so a
      // typo names nothing instead of merging the wrong item. Retired items
      // included: "retire the duplicate, then merge it away" is a reasonable
      // order to do things in, and the list hides them by default.
      if (!loserId) {
        const q = typedSku.trim();
        const found = (await api.get(
          `/parts-catalog?q=${encodeURIComponent(q)}&page_size=25&include_inactive=true`
        )) as { rows: { id: number; sku: string }[] };
        const hit = (found.rows || []).find(
          (r) => r.sku.toUpperCase() === q.toUpperCase()
        );
        if (!hit) {
          toast.error(`No item has the SKU ${q}. Check the spelling.`);
          setBusy(false);
          return;
        }
        if (hit.id === itemId) {
          toast.error('That is this item. Name the OTHER one.');
          setBusy(false);
          return;
        }
        targetId = hit.id;
      }

      const result = (await api.post(`/parts-catalog/${itemId}/merge`, {
        body: { loser_id: targetId, reason: reason.trim() },
      })) as MergeResult;

      toast.success(
        `${result.loser_sku} merged into ${result.winner.sku}. `
        + 'Its stock, boxes and history are on this item now, and the old code still scans.'
      );
      // A separate, louder message, because it is about hardware rather than about
      // the merge succeeding.
      if (result.batchserials_already_minted?.length) {
        toast.warning(
          `Batch ${result.batchserials_already_minted.join(', ')} is already on minted serial numbers `
          + 'under the old code. Those serials still trace back here, and new flashes use this item '
          + 'batch number, but the two numbers both exist. Worth a look.'
        );
      }
      onMerged(result);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'That merge could not be completed');
      setBusy(false);
    }
  };

  const currentLabel = currentItem ? itemLabel(currentItem) : currentSku;

  return (
    <Dialog
      open
      title={`Merge another item into ${currentLabel}`}
      onClose={onClose}
      size="lg"
      busy={busy}
      dirty={!!loserId || typedSku !== '' || reason !== ''}
      footer={
        <>
          <button type="button" onClick={onClose} className={buttonClass('quiet')}>
            Cancel
          </button>
          <button type="button" disabled={!canSubmit} onClick={submit} className={buttonClass('filled')}>
            {busy
              ? 'Merging…'
              : chosen
                ? `Merge ${itemLabel(chosen)} into this`
                : 'Merge'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-on-surface-variant leading-relaxed">
          For two catalog rows that are one physical item, usually the same part entered
          twice under slightly different codes. Everything on the other item moves here:
          its stock is added to this item's, its boxes and purchase-order lines come with
          it, and its code is kept as a former name so labels already printed still scan.
        </p>
        <Alert tone="warning">
          The other item is deleted, and that part cannot be undone. Its history is not
          lost, it is filed under this item.
        </Alert>

        <Reveal show={siblings.length > 0}>
          <div>
            <RadioGroup
              label="Items that look like this one"
              name="merge-loser"
              layout="column"
              value={loserId || null}
              onChange={(id) => {
                setLoserId(id);
                setTypedSku('');
              }}
            >
              {siblings.map((sib) => (
                <RadioRow key={sib.id} value={String(sib.id)}>
                  {/* Name first, code underneath. This is one of the few
                      places the SKU still earns its line: the whole decision is
                      about two codes for one thing. */}
                  <ItemName item={sib} size="sm" showSku />
                  {sib.exact && (
                    <span className="mt-0.5 block text-[10px] uppercase tracking-wider text-red-300">
                      same code
                    </span>
                  )}
                </RadioRow>
              ))}
            </RadioGroup>
          </div>
        </Reveal>

        <label className="block">
          <span className={labelClass}>
            {siblings.length > 0 ? 'Or type another SKU' : 'SKU of the item to merge in'}
          </span>
          <Field
            value={typedSku}
            placeholder="FF-RTK-02-2"
            onChange={(e) => {
              setTypedSku(e.target.value);
              setLoserId('');
            }}
          />
        </label>

        <label className="block">
          <span className={labelClass}>Why are these one item</span>
          <Field
            value={reason}
            placeholder="Same board, revision written two ways"
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="text-[11px] text-on-surface-variant mt-1">
            This is the only record of the decision, on an item whose history will show
            two codes.
          </p>
        </label>
      </div>
    </Dialog>
  );
};

export default MergeItemModal;
